<template>
  <v-card>
    <v-progress-linear
      indeterminate
      absolute
      top
      color="primary"
      :active="loading"
    />

    <v-card-title class="flex justify-space-between">
      {{ value ? 'Изменение иконки' : 'Новая иконка' }}

      <v-img
        v-if="value && value.file"
        :src="value.file.path"
        max-width="48px"
        max-height="48px"
      />
    </v-card-title>

    <v-card-text>
      <v-form ref="form" lazy-validation>
        <v-text-field
          v-model="inputName"
          label="Название иконки"
          :autofocus="!value"
          :rules="rules.name"
          :disabled="loading"
        />

        <v-textarea
          v-model="inputDescription"
          auto-grow
          label="Описание иконки"
          rows="1"
          :disabled="loading"
        />

        <tags-selector
          :items="tags"
          :initial-selected="value && value.tags ? value.tags : []"
          @onInput="inputTags = $event"
        />

        <v-file-input
          v-model="inputFile"
          label="Файл изображения"
          accept="image/png"
          class="pt-1 mt-0"
          placeholder="Выберите файл для загрузки"
          :rules="rules.file"
          :disabled="loading"
          @change="needUploadFile = true"
        />
      </v-form>
    </v-card-text>

    <v-card-actions class="px-6">
      <v-btn
        text
        color="success"
        :disabled="loading"
        @click.prevent="submit"
      >
        {{ !!value ? 'Обновить' : 'Создать' }}
      </v-btn>

      <v-btn
        v-if="value"
        color="error"
        text
        :disabled="loading"
        @click.prevent="removeIcon"
      >
        Удалить
      </v-btn>
      <div class="flex-grow-1" />
      <v-btn
        color="grey darken-1"
        text
        :disabled="loading"
        @click="$emit('close')"
      >
        Отмена
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import { uploadFile } from '@/utils/requests/upload';
import TagsSelector from '@/views/tags/components/TagsSelector.vue';

export default {
  name: 'icon-form',

  components: { TagsSelector },

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },

    tags: {
      type: Array,
      default: () => ([]),
    },
  },

  data() {
    const inputFile = this.value?.file
      ? new File([''], this.value.file.name, { type: this.value.file.type })
      : null;

    return {
      inputFile,
      needUploadFile: false,

      inputName: this.value?.name,
      inputDescription: this.value?.description || '',
      inputTags: this.value?.tags || [],

      loading: false,
      deleting: false,

      rules: {
        name: [v => !!v || 'Поле обязательно для заполнения'],
        file: [v => !!v || 'Необходимо выбрать файл'],
      },
    };
  },

  methods: {
    ...mapActions(['showNotify']),
    ...mapActions('icons', ['create', 'update', 'remove']),
    ...mapActions('tags', { createTags: 'massCreate' }),

    async createTagsIfNeed() {
      if (!this.inputTags.length) return [];

      try {
        return await this.createTags(this.inputTags.map(i => i.name || i));
      } catch (error) {
        throw new Error('Ошибка создания тегов');
      }
    },

    async uploadFileIfNeed() {
      if (!this.needUploadFile) return this.value.file.id;

      try {
        const file = await uploadFile(this.inputFile);

        this.needUploadFile = false;

        return file.id;
      } catch (error) {
        throw new Error('Ошибка загрузки файла');
      }
    },

    async createIcon(payload) {
      try {
        const response = this.value
          ? await this.update({ ...payload, id: this.value.id })
          : await this.create(payload);

        this.showNotify({
          type: 'success',
          message: `Иконка "${response.name}" успешно ${this.value ? 'изменена' : 'создана'}`,
        });

        this.$emit('saved');
      } catch (error) {
        if (error.response.status === 422) {
          throw new Error('Иконка с таким именем уже существует');
        }

        throw new Error('Что-то пошло не так');
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        const tags = await this.createTagsIfNeed();
        const file = await this.uploadFileIfNeed();

        await this.createIcon({
          name: this.inputName,
          description: this.inputDescription,
          tags: tags?.map(i => i.id) || [],
          file,
        });

        this.$emit('saved');
      } catch (error) {
        this.showNotify({
          type: 'error',
          message: error.message,
        });
      } finally {
        this.loading = false;
      }
    },

    async removeIcon() {
      try {
        this.deleting = true;

        await this.remove(this.value.id);

        this.showNotify({
          type: 'success',
          message: `Иконка "${this.value.name}" успешно удалена`,
        });
      } catch (e) {
        this.showNotify({
          type: 'error',
          message: 'Что-то пошло не так',
        });
      } finally {
        this.deleting = false;
      }

      this.$emit('deleted');
    },
  },
};
</script>
